import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import DoctorFeeOrderInterfaceSerializer from "issara-sdk/types/DoctorFeeOrderListPreviewSerializer_apps_DFC";

// API
import DoctorFeeOrderList from "issara-sdk/apis/DoctorFeeOrderList_apps_DFC";
import DoctorFeeOrderTriggerView from "issara-sdk/apis/DoctorFeeOrderTriggerView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";
import DivisionList from "issara-sdk/apis/DivisionList_core";

export type Props = {

}

export type State = Partial<{
  // seq
  DoctorFeeOrderInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    DoctorFeeOrderInterfaceList: DoctorFeeOrderInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    divisionOptions: any[];
    divisionOptionsUse: any[];
    searchDoctorFeeOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  hn: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: DoctorFeeOrderInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "DoctorFeeOrderInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "TRIGGER";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof DoctorFeeOrderInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof DoctorFeeOrderInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  DoctorFeeOrderInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardDoctorFeeOrderInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const divisionOptions = await GetDivisionList(controller, params);

  controller.setState(
    {
      DoctorFeeOrderInterfaceSequence: {
        ...state.DoctorFeeOrderInterfaceSequence,
        sequenceIndex: "Action",
        divisionOptions,
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // * issue 75105
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const divisionOptions = state.DoctorFeeOrderInterfaceSequence?.divisionOptions;
  const searchDoctorFeeOrder = state.DoctorFeeOrderInterfaceSequence?.searchDoctorFeeOrder;
  const paginatedItems = state.DoctorFeeOrderInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  // * issue 75106
  if (!searchDoctorFeeOrder?.hn) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "กรุณาระบุ HN ก่อนทำรายการ",
      },
    });

    return;
  }

  let data = {
    limit: pageSize,
    offset,
    hn: searchDoctorFeeOrder?.hn,
  };

  const result = await GetDoctorFeeOrderInterfaceData(controller, data);

  if (result?.items) {
    const uniqueDivision = (result.items || [])
      .filter(
        (item: DoctorFeeOrderInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((d: any) => d.requesting_division === item.requesting_division)
      )
      .map((item: DoctorFeeOrderInterfaceSerializer) => item?.requesting_division);

    const filterdDivisionOptions = (divisionOptions || []).filter(division =>
      uniqueDivision.includes(division.id)
    );

    controller.setState({
      DoctorFeeOrderInterfaceSequence: {
        ...state.DoctorFeeOrderInterfaceSequence,
        DoctorFeeOrderInterfaceList: result?.items,
        divisionOptionsUse: filterdDivisionOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveDoctorFeeOrderInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  //! Response ตอน Trigger แล้วได้ "" ทำให้ Skip Condition เลยแสดง Button Status ไม่ถูก
  if (saveResp || saveResp === "") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    DoctorFeeOrderInterfaceSequence: {
      ...state.DoctorFeeOrderInterfaceSequence,
      searchDoctorFeeOrder: { ...FILTER_INIT },
      DoctorFeeOrderInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetDoctorFeeOrderInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'hn' */
  const [resp, err] = await DoctorFeeOrderList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: {
      pk: params.id,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const GetDivisionList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DivisionList.list({
    apiToken: controller.apiToken,
    params: {
      limit: 9999,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items;
};

const SaveDoctorFeeOrderInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DoctorFeeOrderTriggerView.put({
    apiToken: controller.apiToken,
    data: {
      doctor_fee_orders: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof DoctorFeeOrderInterfaceSerializer, string>> = {
  id: "ID",
  doctor_fee_rule_display: "Doctor Fee Rule",
  quantity: "Quantity",
  medical_record: "Medical Record",
  received_employee_display: "Received Employee",
  compensation_price: "Compensation Price",
  final_compensation_price: "Final Compensation Price",
  remark: "Remark",
  requesting_division: "Requesting Division",
  performing_division: "Performing Division",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  edited_at: "วันเวลาที่แก้ไข",
  edited_by: "Edited By",
  edited_by_user_id: "Edited By User ID",
  price_unit: "Price Unit",
  price_total: "Price Total",
  price_reimb: "Price Reimb",
  price_discount: "Price Discount",
  price_copay: "Price Copay",
  price_claimable: "Price Claimable",
  price_non_claimable: "Price Non Claimable",
  price_compensation_max: "Price Compensation Max",
  // claim_payload: "Claim Payload", //*
  claim_result: "Claim Result",
  is_paid: "Is Paid",
  // received_employee: string,
  service_code: "Service Code",
  // doctor_fee_rule: number,
  hn: "HN",
  patient_name: "Patient Name",
  cancelable: "Cancelable",
  perform_datetime: "Perform Datetime",
  active: "Active",
};  

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<DoctorFeeOrderInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof DoctorFeeOrderInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};